import type { StoreCoupon } from '../types/store-coupon';

/** "R$ 5" / "R$ 7,50": sem centavos quando o valor é redondo, como nas vitrines. */
export function formatShortBRL(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return rounded.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: Number.isInteger(rounded) ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

/** Rótulo curto do cupom para o chip: "R$ 5 off", "10% off", "Entrega grátis". */
export function couponShortLabel(coupon: StoreCoupon): string {
  if (coupon.discount_type === 'DELIVERY_FREE') return 'Entrega grátis';
  if (coupon.discount_type === 'PERCENTAGE') return `${Math.round(Number(coupon.discount))}% off`;
  return `${formatShortBRL(Number(coupon.discount))} off`;
}

/** Condições do cupom em uma linha: pedido mínimo, teto e público. */
export function couponConditions(coupon: StoreCoupon): string[] {
  const parts: string[] = [];
  const min = Number(coupon.min_subtotal) || 0;
  if (min > 0) parts.push(`Pedido mínimo ${formatShortBRL(min)}`);
  if (coupon.discount_type === 'PERCENTAGE' && coupon.max_discount && coupon.max_discount > 0) {
    parts.push(`até ${formatShortBRL(Number(coupon.max_discount))}`);
  }
  if (coupon.user_type === 'ONCENEW') parts.push('só na 1ª compra');
  else if (coupon.user_type === 'ONCE') parts.push('1 uso por cliente');
  else if (coupon.user_type === 'CUSTOM') parts.push('uso limitado por cliente');
  if (coupon.availability_label) parts.push(`disponível: ${coupon.availability_label}`);
  return parts;
}

/**
 * Quanto o cupom vale num pedido com este subtotal — só para escolher qual
 * cupom destacar; o desconto de verdade é calculado pela API no checkout.
 */
export function estimateCouponValue(
  coupon: StoreCoupon,
  subtotal: number,
  deliveryFee = 0
): number {
  const discount = Number(coupon.discount) || 0;
  if (coupon.discount_type === 'DELIVERY_FREE') return deliveryFee;
  if (coupon.discount_type === 'PERCENTAGE') {
    const base = Math.max(subtotal, Number(coupon.min_subtotal) || 0);
    const value = (base * discount) / 100;
    return coupon.max_discount && coupon.max_discount > 0
      ? Math.min(value, Number(coupon.max_discount))
      : value;
  }
  return discount;
}

export type CouponProgress = {
  coupon: StoreCoupon;
  /** O subtotal já alcança o pedido mínimo. */
  unlocked: boolean;
  /** Quanto falta para liberar (0 quando liberado). */
  missing: number;
  /** 0–100, para a barra. */
  percent: number;
};

/**
 * Qual cupom mostrar na barra do rodapé:
 * 1. o que a pessoa escolheu (se ainda está na vitrine);
 * 2. senão, o mais valioso já liberado pelo subtotal;
 * 3. senão, o mais perto de ser liberado.
 */
export function pickCouponProgress(
  coupons: StoreCoupon[],
  subtotal: number,
  selectedCode?: string | null,
  deliveryFee = 0
): CouponProgress | null {
  if (!coupons.length) return null;

  const progressOf = (coupon: StoreCoupon): CouponProgress => {
    const min = Number(coupon.min_subtotal) || 0;
    const missing = Math.max(0, Math.round((min - subtotal) * 100) / 100);
    return {
      coupon,
      unlocked: missing === 0,
      missing,
      percent: min > 0 ? Math.min(100, Math.max(0, (subtotal / min) * 100)) : 100,
    };
  };

  const selected = selectedCode
    ? coupons.find(c => c.code.toUpperCase() === selectedCode.toUpperCase())
    : undefined;
  if (selected) return progressOf(selected);

  // PRAT-204: fora do dia/horário o cupom não é destacado sozinho.
  const all = coupons.filter(c => c.available_now !== false).map(progressOf);
  if (!all.length) return null;
  const unlocked = all
    .filter(p => p.unlocked)
    .sort(
      (a, b) =>
        estimateCouponValue(b.coupon, subtotal, deliveryFee) -
        estimateCouponValue(a.coupon, subtotal, deliveryFee)
    );
  if (unlocked.length) return unlocked[0];

  return all.sort((a, b) => a.missing - b.missing)[0];
}

/** Flags do cardápio chegam como boolean, 0/1 ou "0"/"1"/"true". */
export function isFlagOn(value: unknown): boolean {
  return value === true || value === 1 || value === '1' || value === 'true';
}
